import type { CSSProperties, FC, ReactNode } from 'react'

import { Fragment, useEffect, useRef, useState } from 'react'
import {
    Bebas_Neue,
    Caveat,
    Instrument_Serif,
    Oswald,
    Pirata_One,
    Playfair_Display
} from 'next/font/google'

import {
    ArrowRight,
    Forward,
    MessageSquareText,
    Play,
    ThumbsDown,
    ThumbsUp,
    Volume2
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { useScrollFrame } from '@/hooks/use-scroll-frame'
import { PageLink } from '@/components/kit/page-link'
import { Photo } from '@/components/kit/photo'
import { Reveal } from '@/components/kit/reveal'
import { ShowreelModal } from '@/components/home/showreel-modal'
import { START_HREF } from '@/constants/site'

const script = Caveat({ subsets: ['latin'], weight: ['700'] })
const condensed = Oswald({ subsets: ['latin'], weight: ['500', '700'] })
const poster = Bebas_Neue({ subsets: ['latin'], weight: '400' })
const serif = Instrument_Serif({ subsets: ['latin'], weight: '400', style: ['normal', 'italic'] })
const editorial = Playfair_Display({ subsets: ['latin'], weight: ['700', '800'], style: ['normal', 'italic'] })
const gothic = Pirata_One({ subsets: ['latin'], weight: '400' })

const CARD_SIZES = '(min-width: 1280px) 230px, (min-width: 1024px) 18vw, (min-width: 640px) 31vw, 48vw'

const HEADLINE_LINES = ['Scale your brand', 'on social media']

const shadowText = '[text-shadow:0_2px_18px_rgb(0_0_0/0.25)]'

/* ---------- headline ---------- */

const BlurHeadline: FC<{ id: string, delay?: number, className?: string }> = ({ id, delay = 0, className }) => {
    const { ref, inView } = useInView<HTMLHeadingElement>()
    let index = 0

    return (
        <h1
            ref={ref}
            id={id}
            data-blur=''
            data-revealed={inView ? '' : undefined}
            className={className}
            style={{ '--delay': `${delay}ms` } as CSSProperties}
        >
            {HEADLINE_LINES.map((line, lineIndex) => {
                const words = line.split(' ')

                return (
                    <span
                        key={line}
                        data-exit=''
                        className='block will-change-[filter]'
                    >
                        {words.map((word, wordIndex) => {
                            const order = index++

                            return (
                                <Fragment key={`${word}-${wordIndex}`}>
                                    <span
                                        className='blur-word'
                                        style={{ '--i': order } as CSSProperties}
                                    >
                                        {word}
                                    </span>
                                    {wordIndex < words.length - 1 ? ' ' : null}
                                </Fragment>
                            )
                        })}
                        {lineIndex < HEADLINE_LINES.length - 1 ? ' ' : null}
                    </span>
                )
            })}
        </h1>
    )
}

/* ---------- card chrome ---------- */

const PlayBadge: FC = () => {
    return (
        <span className='absolute right-3 top-3 z-10 flex size-5 items-center justify-center rounded-[6px] bg-white/90'>
            <Play className='size-2.5 translate-x-px fill-ink text-ink' />
        </span>
    )
}

const MediaControls: FC = () => {
    return (
        <span className='absolute left-3 top-3 z-10 flex gap-1.5'>
            <span className='flex size-7 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm'>
                <Play className='size-3 translate-x-px fill-current' />
            </span>
            <span className='flex size-7 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm'>
                <Volume2 className='size-3.5' />
            </span>
        </span>
    )
}

const SideActions: FC = () => {
    return (
        <span className='absolute bottom-3 right-2.5 z-10 flex flex-col gap-2'>
            {[ThumbsUp, ThumbsDown, MessageSquareText, Forward].map((Icon, index) => (
                <span
                    key={index}
                    className='flex size-7 items-center justify-center rounded-full bg-black/35 text-white backdrop-blur-sm'
                >
                    <Icon className='size-3.5' />
                </span>
            ))}
        </span>
    )
}

type Badge = 'play' | 'controls' | 'none'

interface CardFrameProps {
    src: string
    position?: string
    tint?: string
    badge?: Badge
    actions?: boolean
    place?: string
    preload?: boolean
    imgClassName?: string
    children?: ReactNode
}

const CardInner: FC<CardFrameProps> = ({
    src,
    position,
    tint = 'bg-black/20',
    badge = 'play',
    actions = false,
    place = 'justify-center',
    preload = false,
    imgClassName,
    children
}) => {
    return (
        <>
            <Photo
                src={src}
                alt=''
                sizes={CARD_SIZES}
                position={position}
                preload={preload}
                tone={false}
                className='absolute inset-0'
                imgClassName={imgClassName}
            />
            {tint !== 'none' && <span className={cn('absolute inset-0', tint)} />}
            {badge === 'play' && <PlayBadge />}
            {badge === 'controls' && <MediaControls />}
            {actions && <SideActions />}
            <span className={cn('absolute inset-0 flex flex-col items-center px-[9cqw] py-[10cqw] text-center', place)}>
                {children}
            </span>
        </>
    )
}

const cardShell = '@container relative block aspect-[5/6] w-full overflow-hidden rounded-3xl bg-sand'

const Card: FC<CardFrameProps & { className?: string }> = ({ className, ...props }) => {
    return (
        <div
            aria-hidden='true'
            className={cn(cardShell, className)}
        >
            <CardInner {...props} />
        </div>
    )
}

/* ---------- the wall ---------- */

const buildColumns = (openShowreel: () => void): Array<Array<ReactNode>> => [
    [
        <Card
            key='podcast'
            src='/images/hero/podcast.jpg'
            tint='bg-black/30'
            place='justify-end pb-[16cqw]'
        >
            <span className={cn('text-[length:10cqw] font-medium leading-[1.05] tracking-[-0.03em] text-white', shadowText)}>
                what makes content actually work?
            </span>
            <span className='mt-[3cqw] text-[length:4.6cqw] font-medium text-white/90'>
                Here&apos;s what top creators have to say...
            </span>
        </Card>,
        <Card
            key='latte'
            src='/images/hero/latte.jpg'
            tint='none'
            position='50% 35%'
            place='justify-start pt-[12cqw]'
        >
            <span className={cn(script.className, 'text-[length:16cqw] leading-[0.85] text-ink')}>
                Practical morning ritual
            </span>
        </Card>,
        <Card
            key='cabin'
            src='/images/hero/cabin.jpg'
            tint='bg-black/25'
            badge='controls'
            actions
        >
            <span className={cn(serif.className, 'text-[length:14cqw] italic leading-[0.95] text-white', shadowText)}>
                Welcome to the north!
            </span>
            <span className='mt-[22cqw] text-[length:7cqw] font-bold leading-[1.05] tracking-[-0.03em] text-white'>
                Tips for affordable cabins
            </span>
        </Card>,
        <Card
            key='run'
            src='/images/hero/run.jpg'
            tint='bg-black/20'
            position='70% 40%'
            place='justify-end'
            className='hidden lg:block'
        >
            <span className={cn(condensed.className, 'text-[length:13cqw] font-bold uppercase leading-none tracking-[-0.01em] text-white', shadowText)}>
                5am club, day 30
            </span>
        </Card>
    ],
    [
        <Card
            key='rooftop'
            src='/images/hero/rooftop.jpg'
            tint='bg-black/20'
            position='50% 40%'
        >
            <span className={cn(gothic.className, 'text-[length:17cqw] leading-[0.95] text-white', shadowText)}>
                Thinking like a creator
            </span>
        </Card>,
        <Card
            key='arctic'
            src='/images/hero/arctic.jpg'
            tint='bg-black/20'
            badge='controls'
            actions
        >
            <span className={cn(condensed.className, 'pr-[8cqw] text-[length:12cqw] font-medium leading-[1] tracking-[-0.03em] text-white', shadowText)}>
                Enjoying a boat trip in the Arctic
            </span>
        </Card>,
        <Card
            key='concert'
            src='/images/hero/concert.jpg'
            tint='none'
            place='justify-start items-start text-left pt-[14cqw]'
        >
            <span className='text-[length:15cqw] font-bold leading-[1] tracking-[-0.04em] text-ink'>
                Record. Edit. Post.
            </span>
        </Card>,
        <Card
            key='beauty'
            src='/images/hero/beauty.jpg'
            tint='bg-black/25'
            badge='none'
            className='hidden lg:block'
        >
            <span className={cn(editorial.className, 'text-[length:10cqw] font-extrabold leading-[1.05] text-white', shadowText)}>
                Easy glow routine in 1 min
            </span>
            <span className='absolute bottom-[7cqw] left-[7cqw] flex items-center gap-1.5 text-[length:7cqw] font-medium text-white'>
                <Play className='size-[1em]' />
                170.6k
            </span>
        </Card>
    ],
    [
        <Card
            key='canal'
            src='/images/hero/canal.jpg'
            tint='bg-black/20'
            place='justify-end pb-[24cqw]'
        >
            <span className={cn('font-heading text-[length:14cqw] font-bold leading-[0.95] tracking-[-0.03em] text-white', shadowText)}>
                How should brands think?
            </span>
        </Card>,
        <Card
            key='bag'
            src='/images/hero/bag.jpg'
            tint='bg-black/15'
            position='50% 45%'
        >
            <span className={cn(serif.className, 'text-[length:20cqw] leading-[0.9] tracking-[-0.03em] text-white', shadowText)}>
                bag guide 101
            </span>
            <span className='absolute inset-x-[8cqw] bottom-[9cqw] text-[length:7cqw] leading-[1.1] text-white'>
                How to choose the right bag?
            </span>
        </Card>,
        <Card
            key='forest'
            src='/images/hero/forest.jpg'
            tint='bg-black/35'
        >
            <span className={cn(condensed.className, 'text-[length:6.5cqw] font-bold tracking-[-0.02em] text-white')}>
                Not where. When.
            </span>
            <span className={cn(poster.className, 'mt-[2cqw] text-[length:30cqw] leading-[0.85] text-white')}>
                DARK
            </span>
        </Card>,
        <Card
            key='space'
            src='/images/hero/space.jpg'
            tint='bg-black/30'
            place='justify-between'
            className='hidden lg:block'
        >
            <span className={cn(serif.className, 'text-[length:15cqw] leading-[0.95] tracking-[-0.02em] text-white')}>
                Alone on Mars, day 214
            </span>
            <span className={cn(serif.className, 'text-[length:8cqw] leading-[1.05] text-white/90')}>
                But the view is worth it :)
            </span>
        </Card>
    ],
    [
        <Card
            key='selfie'
            src='/images/hero/selfie.jpg'
            tint='none'
            position='50% 25%'
            place='justify-center pt-[30cqw]'
            preload
        >
            <span className={cn(script.className, 'text-[length:22cqw] leading-[0.8] text-ink')}>
                Instagram
            </span>
        </Card>,
        <Card
            key='speaker'
            src='/images/hero/speaker.jpg'
            tint='bg-black/20'
            place='justify-end pb-[14cqw]'
        >
            <span className={cn('text-[length:10cqw] font-medium leading-[1.05] tracking-[-0.02em] text-white', shadowText)}>
                What drives true human influence
            </span>
        </Card>,
        <Card
            key='dinner'
            src='/images/hero/dinner.jpg'
            tint='bg-black/20'
            badge='controls'
            actions
        >
            <span className={cn(editorial.className, 'pr-[8cqw] text-[length:13cqw] font-extrabold italic leading-[0.95] text-white', shadowText)}>
                most interesting dishes
            </span>
        </Card>,
        <Card
            key='couple'
            src='/images/hero/couple.jpg'
            tint='none'
            badge='controls'
            className='hidden lg:block'
        >
            <span className={cn(gothic.className, 'text-[length:18cqw] leading-[0.9] text-ink')}>
                maybe not today
            </span>
        </Card>
    ],
    [
        <Card
            key='street'
            src='/images/hero/street.jpg'
            tint='none'
            position='50% 30%'
            place='justify-end pb-[9cqw]'
            preload
        >
            <span className={cn(serif.className, 'text-[length:13cqw] leading-[0.95] tracking-[-0.02em] text-ink')}>
                This is my summer look
            </span>
            <span className={cn(serif.className, 'mt-[2cqw] text-[length:5.5cqw] text-ink/80')}>
                Clear frames, Studio Nord
            </span>
        </Card>,
        <div
            key='live'
            className={cn(cardShell, 'group')}
        >
            <button
                type='button'
                onClick={openShowreel}
                data-cursor='Play'
                aria-label='Watch the Updraft showreel'
                className='absolute inset-0 z-20 rounded-3xl focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-white'
            />
            <div
                aria-hidden='true'
                className='absolute inset-0'
            >
                <CardInner
                src='/images/hero/live.jpg'
                position='50% 30%'
                tint='bg-black/25'
                badge='controls'
                actions
                preload
                imgClassName='transition-transform duration-[1.2s] ease-expo group-hover:scale-[1.05]'
            >
                <span className={cn(poster.className, 'pr-[8cqw] text-[length:21cqw] leading-[0.88] text-white', shadowText)}>
                    Live set 2026
                </span>
                <span className='mt-[3cqw] rounded-full bg-ember px-[4cqw] py-[1.5cqw] text-[length:5.5cqw] font-medium text-white'>
                    Watch our showreel
                </span>
                </CardInner>
            </div>
        </div>,
        <Card
            key='tennis'
            src='/images/hero/tennis.jpg'
            tint='bg-black/10'
            place='justify-start pt-[10cqw]'
        >
            <span className={cn(script.className, 'text-[length:20cqw] uppercase leading-[0.85] text-white', shadowText)}>
                Let&apos;s play
            </span>
        </Card>,
        <Card
            key='stadium'
            src='/images/hero/stadium.jpg'
            tint='bg-black/30'
            className='hidden lg:block'
        >
            <span className={cn(poster.className, 'text-[length:22cqw] leading-[0.88] text-white')}>
                Night shift
            </span>
            <span className={cn(condensed.className, 'absolute bottom-[8cqw] text-[length:6cqw] font-medium text-white/85')}>
                City lights, 22:00
            </span>
        </Card>
    ]
]

/*
 * Staircase on large screens: each column starts one step lower than the one on its right.
 * A card is 1.2x the column width (5:6), and one step is 52% of a card, so 62.4% of the
 * column width. Percentage padding on a grid item resolves against the column width.
 */
const COLUMN_CLASSES = [
    'hidden lg:flex lg:pt-[249.6%]',
    'hidden lg:flex lg:pt-[187.2%]',
    'hidden sm:flex max-lg:pt-10 lg:pt-[124.8%]',
    'flex lg:pt-[62.4%]',
    'flex max-lg:pt-10'
]

const STEP = 0.624

/* While scrolling, each step closes at 0.135px per px scrolled; the right-most column lags at 0.41. */
const CLOSE_RATE = 0.135
const LAG = 0.41

/* ---------- hero ---------- */

export const Hero: FC = () => {
    const [showreelOpen, setShowreelOpen] = useState(false)
    const reduced = useReducedMotion()
    const sectionRef = useRef<HTMLElement>(null)
    const columnRefs = useRef<Array<HTMLDivElement | null>>([])
    const textRef = useRef<HTMLDivElement>(null)

    const update = () => {
        const section = sectionRef.current
        const right = columnRefs.current[4]

        if (!section || !right) return

        const scrolled = window.scrollY - section.offsetTop
        const wide = window.innerWidth >= 1024
        const animate = wide && !reduced
        const distance = (right.offsetWidth * STEP) / CLOSE_RATE
        const travel = Math.min(distance, Math.max(0, scrolled))

        columnRefs.current.forEach((column, index) => {
            if (!column) return

            column.style.transform = animate
                ? `translate3d(0, ${(travel * (LAG - (4 - index) * CLOSE_RATE)).toFixed(1)}px, 0)`
                : ''
        })

        if (reduced) return

        textRef.current?.querySelectorAll<HTMLElement>('[data-exit]').forEach(element => {
            const top = element.getBoundingClientRect().top
            const amount = Math.min(1, Math.max(0, (120 - top) / 130))

            element.style.filter = amount > 0 ? `blur(${(amount * 12).toFixed(1)}px)` : ''
            element.style.opacity = amount > 0 ? (1 - amount * 0.55).toFixed(3) : ''
        })
    }

    useScrollFrame(update)

    useEffect(() => {
        update()
    })

    const columns = buildColumns(() => setShowreelOpen(true))

    return (
        <section
            ref={sectionRef}
            id='hero'
            aria-labelledby='hero-title'
            className='relative isolate overflow-hidden bg-cream'
        >
            <div className='relative mx-auto w-full max-w-[1280px] px-5 md:px-10'>
                <div
                    ref={textRef}
                    className='relative z-10 pt-28 md:pt-36 lg:pointer-events-none lg:absolute lg:inset-x-10 lg:top-0 lg:pt-[150px]'
                >
                    <div className='lg:pointer-events-auto lg:w-fit'>
                        <Reveal y={14}>
                            <div data-exit=''>
                                <PageLink
                                    href='/journal/forecast-early-2027'
                                    className='group inline-flex items-center gap-2.5 rounded-lg border border-black/5 bg-dune py-2 pl-3.5 pr-3 text-[15px] text-ink transition-colors duration-300 hover:bg-sand'
                                >
                                    <span className='size-2 rounded-full bg-ember' />
                                    See 2026 social media trends
                                    <ArrowRight className='size-4 transition-transform duration-500 ease-expo group-hover:translate-x-0.5' />
                                </PageLink>
                            </div>
                        </Reveal>

                        <BlurHeadline
                            id='hero-title'
                            delay={120}
                            className='mt-7 font-heading text-[clamp(3rem,10.5vw,5.5rem)] font-bold leading-[0.95] tracking-[-0.045em] text-ink'
                        />

                        <Reveal
                            delay={650}
                            className='mt-7 max-w-[37rem]'
                        >
                            <p
                                data-exit=''
                                className='text-xl leading-[1.45] tracking-[-0.01em] text-ink md:text-[1.375rem]'
                            >
                                High performing social media strategies that grow your audience and drive real business results.
                            </p>
                        </Reveal>

                        <Reveal
                            delay={780}
                            className='mt-7'
                        >
                            <div data-exit=''>
                                <PageLink
                                    href={START_HREF}
                                    className='inline-flex h-12 items-center rounded-[14px] bg-ember px-6 text-[17px] font-medium text-white transition-[background-color,scale] duration-300 hover:bg-[#ff6a14] active:scale-[0.98]'
                                >
                                    <span className='swap-label'>
                                        <span>Start for free</span>
                                        <span aria-hidden='true'>Start for free</span>
                                    </span>
                                </PageLink>
                            </div>
                        </Reveal>

                        <Reveal
                            delay={900}
                            className='mt-6'
                        >
                            <p
                                data-exit=''
                                className='text-sm text-ink'
                            >
                                No contracts. Just growth.
                            </p>
                        </Reveal>
                    </div>
                </div>

                <div className='grid grid-cols-2 items-start gap-3 pb-16 pt-14 sm:grid-cols-3 sm:gap-4 lg:grid-cols-5 lg:pb-10 lg:pt-[272px]'>
                    {columns.map((cards, index) => (
                        <div
                            key={index}
                            ref={element => {
                                columnRefs.current[index] = element
                            }}
                            className={cn('flex-col will-change-transform', COLUMN_CLASSES[index])}
                        >
                            <Reveal
                                delay={200 + (4 - index) * 110}
                                y={70}
                                className='flex flex-col gap-3 sm:gap-4'
                            >
                                {cards}
                            </Reveal>
                        </div>
                    ))}
                </div>
            </div>

            <ShowreelModal
                open={showreelOpen}
                onClose={() => setShowreelOpen(false)}
            />
        </section>
    )
}
